import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { useMsal } from '@azure/msal-react'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

interface RoleContextType {
  isAdmin: boolean
  isAuthorized: boolean
  isLoading: boolean
  userEmail: string
  userDisplayName: string
  error: string | null
  refetch: () => Promise<void>
}

const RoleContext = createContext<RoleContextType>({
  isAdmin: false,
  isAuthorized: false,
  isLoading: true,
  userEmail: '',
  userDisplayName: '',
  error: null,
  refetch: async () => {},
})

export function useRole() {
  return useContext(RoleContext)
}

interface RoleProviderProps {
  children: ReactNode
}

export function RoleProvider({ children }: RoleProviderProps) {
  const { accounts } = useMsal()
  const { getAccessToken, isAuthenticated } = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [userEmail, setUserEmail] = useState('')
  const [userDisplayName, setUserDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const fetchRole = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const token = await getAccessToken()
      const response = await api.getCurrentUser(token)

      // Response fields are directly on response, not nested under 'user'
      setIsAdmin(response.isAdmin || false)
      setIsAuthorized(response.isAuthorized || false)
      setUserEmail(response.email || accounts[0]?.username || '')
      setUserDisplayName(response.displayName || accounts[0]?.name || '')
    } catch (err) {
      console.error('Failed to fetch user role:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user role'
      setError(errorMessage)
      // If we get a 403 Forbidden, the user is not authorized
      if (errorMessage.includes('Forbidden') || errorMessage.includes('not authorized')) {
        setIsAuthorized(false)
        setIsAdmin(false)
      }
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, accounts, getAccessToken])

  useEffect(() => {
    fetchRole()
  }, [fetchRole])

  return (
    <RoleContext.Provider
      value={{
        isAdmin,
        isAuthorized,
        isLoading,
        userEmail,
        userDisplayName,
        error,
        refetch: fetchRole,
      }}
    >
      {children}
    </RoleContext.Provider>
  )
}
